import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import '../styles/DoctorProfile.css';

const DoctorProfile = ({ onProfileUpdate }) => {
  const [profile, setProfile] = useState(null);
  const [specializations, setSpecializations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    specialization: '',
    yearsOfExperience: '',
    consultationFee: '',
    bio: '',
    languages: [],
    education: [],
    gender: '',
    bmdcRegistrationNumber: '',
    bmdcRegistrationCertificate: ''
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [tempProfilePicture, setTempProfilePicture] = useState(null);
  const [profilePictureError, setProfilePictureError] = useState(false);

  useEffect(() => {
    // Ensure token is set in API headers on mount/refresh
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // #region agent log
      const hasAuthHeader = !!api.defaults.headers.common['Authorization'];
      const logData = {
        location: 'DoctorProfile.js:useEffect-mount',
        message: 'Component mounted - setting auth header',
        data: {
          hasToken: !!token,
          tokenLength: token?.length,
          hasAuthHeader: hasAuthHeader
        },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'session-debug',
        hypothesisId: 'DP1'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData)
      }).catch(() => {});
      // #endregion
    } else {
      // #region agent log
      const localStorageKeys = Object.keys(localStorage);
      const logData2 = {
        location: 'DoctorProfile.js:useEffect-mount-notoken',
        message: 'Component mounted - NO TOKEN',
        data: { localStorageKeys: localStorageKeys },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'session-debug',
        hypothesisId: 'DP2'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData2)
      }).catch(() => {});
      // #endregion
    }
    fetchProfile();
    fetchSpecializations();
    initializeTheme();
  }, []);

  const initializeTheme = () => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const isDark = savedTheme === 'dark';
    setIsDarkMode(isDark);
    document.documentElement.setAttribute('data-theme', savedTheme);
  };

  useEffect(() => {
    const handleThemeChange = () => {
      const currentTheme = document.documentElement.getAttribute('data-theme');
      setIsDarkMode(currentTheme === 'dark');
    };
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          handleThemeChange();
        }
      });
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    return () => observer.disconnect();
  }, []);

  const constructProfilePictureUrl = (picturePath) => {
    // #region agent log
    const logData = {
      location: 'DoctorProfile.js:constructProfilePictureUrl',
      message: 'Constructing profile picture URL',
      data: {
        picturePath,
        isCloudinary: picturePath?.includes('cloudinary'),
        isHttp: picturePath?.startsWith('http'),
        isDataUri: picturePath?.startsWith('data:'),
        isUploadsPath: picturePath?.startsWith('/uploads/')
      },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'image-debug',
      hypothesisId: 'IMG1'
    };
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logData)
    }).catch(() => {});
    // #endregion
    
    if (!picturePath) return null;
    // Cloudinary URLs should be passed through as-is
    if (picturePath.startsWith('http://') || picturePath.startsWith('https://') || picturePath.startsWith('data:')) {
      // #region agent log
      const logData2 = {
        location: 'DoctorProfile.js:constructProfilePictureUrl-return',
        message: 'Returning URL as-is',
        data: { picturePath, returnedUrl: picturePath },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'image-debug',
        hypothesisId: 'IMG2'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData2)
      }).catch(() => {});
      // #endregion
      return picturePath;
    }
    if (picturePath.startsWith('/uploads/')) {
      // #region agent log
      const logData3 = {
        location: 'DoctorProfile.js:constructProfilePictureUrl-uploads',
        message: 'Returning uploads path as-is',
        data: { picturePath, returnedUrl: picturePath },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'image-debug',
        hypothesisId: 'IMG3'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logData3)
      }).catch(() => {});
      // #endregion
      return picturePath;
    }
    const baseUrl = process.env.REACT_APP_API_URL || 'https://weheal-backend.onrender.com';
    const cleanPath = picturePath.replace(/^\//, '');
    const constructedUrl = `${baseUrl}/${cleanPath}`;
    // #region agent log
    const logData4 = {
      location: 'DoctorProfile.js:constructProfilePictureUrl-constructed',
      message: 'Constructed URL with baseUrl',
      data: { picturePath, baseUrl, constructedUrl },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'image-debug',
      hypothesisId: 'IMG4'
    };
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logData4)
    }).catch(() => {});
    // #endregion
    return constructedUrl;
  };

  const fetchProfile = async () => {
    try {
      // Ensure token is set before fetching
      const token = localStorage.getItem('token');
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      setLoading(true);
      // #region agent log
      const logDataFetch1 = {
        location: 'DoctorProfile.js:fetchProfile-start',
        message: 'Fetching profile',
        data: { hasToken: !!token, hasAuthHeader: !!api.defaults.headers.common['Authorization'] },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'session-debug',
        hypothesisId: 'FETCH1'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataFetch1) }).catch(() => {});
      // #endregion
      
      const response = await api.get('/doctor/profile');
      
      // #region agent log
      const logDataFetch2 = {
        location: 'DoctorProfile.js:fetchProfile-response',
        message: 'Profile response received',
        data: { success: response.data.success, hasProfilePicture: !!response.data.data?.profilePicture, profilePicture: response.data.data?.profilePicture },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'session-debug',
        hypothesisId: 'FETCH2'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataFetch2) }).catch(() => {});
      // #endregion
      
      if (response.data.success) {
        setProfile(response.data.data);
        setFormData({
          name: response.data.data.name || '',
          phone: response.data.data.phone || '',
          specialization: response.data.data.doctorDetails?.specialization || '',
          yearsOfExperience: response.data.data.doctorDetails?.yearsOfExperience || '',
          consultationFee: response.data.data.doctorDetails?.consultationFee || '',
          bio: response.data.data.doctorDetails?.bio || '',
          languages: response.data.data.doctorDetails?.languages || [],
          education: response.data.data.doctorDetails?.education || [],
          gender: response.data.data.doctorDetails?.gender || '',
          bmdcRegistrationNumber: response.data.data.doctorDetails?.bmdcRegistrationNumber || '',
          bmdcRegistrationCertificate: response.data.data.doctorDetails?.bmdcRegistrationCertificate || ''
        });
        if (response.data.data.profilePicture) {
          const pictureUrl = constructProfilePictureUrl(response.data.data.profilePicture);
          setProfilePicture(pictureUrl);
          setProfilePictureError(false);
        } else {
          setProfilePicture(null);
          setProfilePictureError(false);
        }
        setError(null);
      }
    } catch (err) {
      // #region agent log
      const logDataFetch3 = {
        location: 'DoctorProfile.js:fetchProfile-error',
        message: 'Profile fetch error',
        data: { error: err.message, status: err.response?.status, statusText: err.response?.statusText, is401: err.response?.status === 401 },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'session-debug',
        hypothesisId: 'FETCH3'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataFetch3) }).catch(() => {});
      // #endregion
      setError(err.response?.data?.message || 'Error loading profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchSpecializations = async () => {
    try {
      const response = await api.get('/doctor/specializations');
      if (response.data.success) {
        setSpecializations(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching specializations:', err);
      setSpecializations([
        'General Medicine', 'Cardiology', 'Dermatology', 'Endocrinology',
        'Gastroenterology', 'Neurology', 'Obstetrics & Gynecology', 'Ophthalmology',
        'Orthopedics', 'Pediatrics', 'Psychiatry', 'Pulmonology', 'Rheumatology',
        'Urology', 'ENT (Ear, Nose & Throat)', 'Emergency Medicine', 'Family Medicine',
        'Internal Medicine', 'Oncology', 'Pathology'
      ]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLanguageChange = (language) => {
    const updatedLanguages = formData.languages.includes(language)
      ? formData.languages.filter(l => l !== language)
      : [...formData.languages, language];
    setFormData(prev => ({ ...prev, languages: updatedLanguages }));
  };

  const handleEducationChange = (index, field, value) => {
    const newEducation = [...formData.education];
    if (!newEducation[index]) {
      newEducation[index] = { degree: '', institution: '', year: '' };
    }
    newEducation[index][field] = value;
    setFormData(prev => ({ ...prev, education: newEducation }));
  };

  const addEducation = () => {
    setFormData(prev => ({
      ...prev,
      education: [...prev.education, { degree: '', institution: '', year: '' }]
    }));
  };

  const removeEducation = (index) => {
    setFormData(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
  };

  const handleProfilePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // #region agent log
    const logDataUpl1 = {
      location: 'DoctorProfile.js:handleProfilePictureUpload-start',
      message: 'Profile picture upload started',
      data: { fileName: file.name, fileSize: file.size, fileType: file.type, hasToken: !!localStorage.getItem('token'), hasAuthHeader: !!api.defaults.headers.common['Authorization'] },
      timestamp: Date.now(),
      sessionId: 'debug-session',
      runId: 'image-debug',
      hypothesisId: 'UPL1'
    };
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataUpl1) }).catch(() => {});
    // #endregion

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Invalid file type. Only JPEG, PNG, and GIF images are allowed.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('File size too large. Maximum size is 5MB.');
      return;
    }

    // Ensure token is set before upload
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      setTempProfilePicture(e.target.result);
    };
    reader.readAsDataURL(file);

    try {
      setUploadingPicture(true);
      const formData = new FormData();
      formData.append('profilePicture', file);

      // #region agent log
      const logDataUpl2 = {
        location: 'DoctorProfile.js:handleProfilePictureUpload-before-request',
        message: 'About to send upload request',
        data: { hasToken: !!token, hasAuthHeader: !!api.defaults.headers.common['Authorization'], endpoint: '/doctor/upload-profile-picture' },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'image-debug',
        hypothesisId: 'UPL2'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataUpl2) }).catch(() => {});
      // #endregion

      const response = await api.post('/doctor/upload-profile-picture', formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}` // Explicitly set in request
        },
      });

      // #region agent log
      const logDataUpl3 = {
        location: 'DoctorProfile.js:handleProfilePictureUpload-response',
        message: 'Upload response received',
        data: { success: response.data.success, hasProfilePicture: !!response.data.profilePicture, profilePicture: response.data.profilePicture, hasData: !!response.data.data, responseKeys: Object.keys(response.data) },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'image-debug',
        hypothesisId: 'UPL3'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataUpl3) }).catch(() => {});
      // #endregion

      if (response.data.success) {
        let pictureUrl = response.data.profilePicture || response.data.data?.profilePicture || response.data.url;
        // #region agent log
        const logDataUpl4 = {
          location: 'DoctorProfile.js:handleProfilePictureUpload-before-construct',
          message: 'Before constructing URL',
          data: { rawPictureUrl: pictureUrl, isCloudinary: pictureUrl?.includes('cloudinary') },
          timestamp: Date.now(),
          sessionId: 'debug-session',
          runId: 'image-debug',
          hypothesisId: 'UPL4'
        };
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataUpl4) }).catch(() => {});
        // #endregion
        pictureUrl = constructProfilePictureUrl(pictureUrl);
        // #region agent log
        const logDataUpl5 = {
          location: 'DoctorProfile.js:handleProfilePictureUpload-after-construct',
          message: 'After constructing URL',
          data: { constructedUrl: pictureUrl },
          timestamp: Date.now(),
          sessionId: 'debug-session',
          runId: 'image-debug',
          hypothesisId: 'UPL5'
        };
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataUpl5) }).catch(() => {});
        // #endregion
        setProfilePicture(pictureUrl);
        setTempProfilePicture(null);
        setProfile(prev => ({ ...prev, profilePicture: pictureUrl }));
        if (onProfileUpdate) {
          onProfileUpdate(pictureUrl);
        }
        alert('Profile picture updated successfully!');
      } else {
        alert(response.data.message || 'Error updating profile picture');
        setTempProfilePicture(null);
      }
    } catch (err) {
      // #region agent log
      const logDataUpl6 = {
        location: 'DoctorProfile.js:handleProfilePictureUpload-error',
        message: 'Upload error occurred',
        data: { error: err.message, status: err.response?.status, statusText: err.response?.statusText, responseData: err.response?.data },
        timestamp: Date.now(),
        sessionId: 'debug-session',
        runId: 'image-debug',
        hypothesisId: 'UPL6'
      };
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataUpl6) }).catch(() => {});
      // #endregion
      console.error('Error uploading profile picture:', err);
      alert(err.response?.data?.message || 'Error uploading profile picture');
      setTempProfilePicture(null);
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const dataToSubmit = {
        ...formData,
        languages: formData.languages || [],
        education: formData.education.map(edu => ({
          ...edu,
          year: parseInt(edu.year) || null
        }))
      };

      const response = await api.put('/doctor/profile', dataToSubmit);
      if (response.data.success) {
        setProfile(response.data.data);
        setEditMode(false);
        setError(null);
        alert('Profile updated successfully!');
      } else {
        setError(response.data.message || 'Error updating profile');
      }
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.response?.data?.message || 'Error updating profile. Please check your input and try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="doctor-profile-container-modern">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="doctor-profile-container-modern">
      <div className="doctor-profile-header-modern">
        <div className="profile-header-content">
          <h2 className="profile-title-modern">Doctor Profile</h2>
          <p className="profile-subtitle-modern">Manage your professional information</p>
        </div>
        <button 
          className={`btn-profile-edit ${editMode ? 'btn-cancel' : 'btn-edit'}`}
          onClick={() => {
            setEditMode(!editMode);
            if (editMode) {
              fetchProfile();
            }
          }}
        >
          <i data-feather={editMode ? 'x' : 'edit-2'}></i>
          {editMode ? 'Cancel' : 'Edit Profile'}
        </button>
      </div>

      {error && <div className="error-message-modern">{error}</div>}

      {/* Profile Picture Section - Modern */}
      <div className="profile-picture-section-modern">
        <div className="profile-picture-wrapper-modern">
          {tempProfilePicture ? (
            <img 
              src={tempProfilePicture} 
              alt="Profile Preview" 
              className="profile-picture-modern"
              onError={(e) => {
                // #region agent log
                const logDataImg5 = {
                  location: 'DoctorProfile.js:img-temp-error',
                  message: 'Temp profile picture failed to load',
                  data: { src: e.target.src },
                  timestamp: Date.now(),
                  sessionId: 'debug-session',
                  runId: 'image-debug',
                  hypothesisId: 'IMG5'
                };
                fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataImg5) }).catch(() => {});
                // #endregion
                setTempProfilePicture(null);
              }}
            />
          ) : profilePicture && !profilePictureError ? (
            <img 
              src={profilePicture} 
              alt="Profile" 
              className="profile-picture-modern"
              onLoad={() => {
                // #region agent log
                const logDataImg6 = {
                  location: 'DoctorProfile.js:img-load-success',
                  message: 'Profile picture loaded successfully',
                  data: { src: profilePicture, isCloudinary: profilePicture?.includes('cloudinary') },
                  timestamp: Date.now(),
                  sessionId: 'debug-session',
                  runId: 'image-debug',
                  hypothesisId: 'IMG6'
                };
                fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataImg6) }).catch(() => {});
                // #endregion
                setProfilePictureError(false);
              }}
              onError={(e) => {
                // #region agent log
                const logDataImg7 = {
                  location: 'DoctorProfile.js:img-load-error',
                  message: 'Profile picture failed to load',
                  data: { src: e.target.src, currentSrc: e.target.currentSrc, isCloudinary: e.target.src?.includes('cloudinary') },
                  timestamp: Date.now(),
                  sessionId: 'debug-session',
                  runId: 'image-debug',
                  hypothesisId: 'IMG7'
                };
                fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(logDataImg7) }).catch(() => {});
                // #endregion
                console.error('Failed to load profile picture:', e.target.src);
                setProfilePictureError(true);
              }}
            />
          ) : (
            <div className="profile-picture-placeholder-modern">
              <i data-feather="user"></i>
            </div>
          )}
          {editMode && (
            <div className="profile-picture-overlay-modern">
              <label htmlFor="profile-picture-input-modern" className="upload-picture-btn-modern">
                {uploadingPicture ? (
                  <div className="uploading-spinner-modern"></div>
                ) : (
                  <>
                    <i data-feather="camera"></i>
                    <span>{(profilePicture || tempProfilePicture) ? 'Change' : 'Upload'}</span>
                  </>
                )}
              </label>
              {(profilePicture || tempProfilePicture) && (
                <button
                  type="button"
                  className="remove-picture-btn-modern"
                  onClick={() => {
                    setProfilePicture(null);
                    setTempProfilePicture(null);
                  }}
                  disabled={uploadingPicture}
                >
                  <i data-feather="trash-2"></i>
                </button>
              )}
              <input
                id="profile-picture-input-modern"
                type="file"
                accept="image/*"
                onChange={handleProfilePictureUpload}
                style={{ display: 'none' }}
                disabled={uploadingPicture}
              />
            </div>
          )}
        </div>
      </div>

      {/* Concise Profile Form */}
      <form onSubmit={handleSubmit} className="doctor-profile-form-modern">
        <div className="profile-form-container-modern">
          {/* Basic Info */}
          <div className="form-section-modern">
            <h3 className="form-section-title-modern">
              <i data-feather="user"></i>
              Basic Information
            </h3>
            <div className="form-section-content-modern">
              <div className="form-group-modern">
                <label>Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  required
                  placeholder="Dr. John Doe"
                />
              </div>
              <div className="form-group-modern">
                <label>Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  required
                  placeholder="+1 (555) 123-4567"
                />
              </div>
            </div>
          </div>

          {/* Professional Details */}
          <div className="form-section-modern">
            <h3 className="form-section-title-modern">
              <i data-feather="briefcase"></i>
              Professional Details
            </h3>
            <div className="form-section-content-modern">
              <div className="form-group-modern">
                <label>Specialization</label>
                <select
                  name="specialization"
                  value={formData.specialization}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  required
                >
                  <option value="">Select Specialization</option>
                  {specializations.map(spec => (
                    <option key={spec} value={spec}>{spec}</option>
                  ))}
                </select>
              </div>
              <div className="form-row-modern">
                <div className="form-group-modern">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    required
                  >
                    <option value="">Select Gender</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="form-group-modern">
                  <label>Years of Experience</label>
                  <input
                    type="number"
                    name="yearsOfExperience"
                    value={formData.yearsOfExperience}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    min="0"
                    max="50"
                    required
                    placeholder="5"
                  />
                </div>
              </div>
              <div className="form-row-modern">
                <div className="form-group-modern">
                  <label>Consultation Fee ($)</label>
                  <input
                    type="number"
                    name="consultationFee"
                    value={formData.consultationFee}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    min="0"
                    required
                    placeholder="100"
                  />
                </div>
                <div className="form-group-modern">
                  <label>BMDC Registration Number</label>
                  <input
                    type="text"
                    name="bmdcRegistrationNumber"
                    value={formData.bmdcRegistrationNumber}
                    onChange={handleInputChange}
                    disabled={!editMode}
                    required
                    placeholder="Enter BMDC registration number"
                  />
                </div>
              </div>
              {formData.bmdcRegistrationCertificate && (
                <div className="form-group-modern">
                  <label>BMDC Registration Certificate</label>
                  <div className="certificate-preview">
                    <a 
                      href={formData.bmdcRegistrationCertificate} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="certificate-link"
                    >
                      <i data-feather="file"></i>
                      View Certificate
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Education */}
          <div className="form-section-modern">
            <h3 className="form-section-title-modern">
              <i data-feather="graduation-cap"></i>
              Education
            </h3>
            <div className="form-section-content-modern">
              {formData.education.length === 0 && !editMode && (
                <p className="no-education-text">No education information added yet.</p>
              )}
              {formData.education.map((edu, index) => (
                <div key={index} className="education-entry-modern">
                  <div className="education-entry-header">
                    <span className="education-entry-number">Education #{index + 1}</span>
                    {editMode && (
                      <button
                        type="button"
                        className="remove-education-btn-modern"
                        onClick={() => removeEducation(index)}
                      >
                        <i data-feather="trash-2"></i>
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="education-fields-modern">
                    <div className="form-group-modern">
                      <label>Degree</label>
                      <input
                        type="text"
                        value={edu.degree || ''}
                        onChange={(e) => handleEducationChange(index, 'degree', e.target.value)}
                        disabled={!editMode}
                        placeholder="e.g., MBBS, MD, PhD"
                        required
                      />
                    </div>
                    <div className="form-group-modern">
                      <label>Institution/Place</label>
                      <input
                        type="text"
                        value={edu.institution || ''}
                        onChange={(e) => handleEducationChange(index, 'institution', e.target.value)}
                        disabled={!editMode}
                        placeholder="e.g., Harvard Medical School"
                        required
                      />
                    </div>
                    <div className="form-group-modern">
                      <label>Year</label>
                      <input
                        type="number"
                        value={edu.year || ''}
                        onChange={(e) => handleEducationChange(index, 'year', e.target.value)}
                        disabled={!editMode}
                        min="1900"
                        max={new Date().getFullYear()}
                        placeholder="e.g., 2015"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
              {editMode && (
                <button
                  type="button"
                  className="add-education-btn-modern"
                  onClick={addEducation}
                >
                  <i data-feather="plus"></i>
                  Add Education
                </button>
              )}
            </div>
          </div>

          {/* Languages */}
          <div className="form-section-modern">
            <h3 className="form-section-title-modern">
              <i data-feather="globe"></i>
              Languages Spoken
            </h3>
            <div className="form-section-content-modern">
              <div className="languages-grid-modern">
                {['English', 'Spanish', 'French', 'German', 'Chinese', 'Hindi', 'Arabic', 'Bangla'].map(lang => (
                  <label key={lang} className="language-chip-modern">
                    <input
                      type="checkbox"
                      checked={formData.languages.includes(lang)}
                      onChange={() => handleLanguageChange(lang)}
                      disabled={!editMode}
                    />
                    <span>{lang}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="form-section-modern">
            <h3 className="form-section-title-modern">
              <i data-feather="file-text"></i>
              Professional Bio
            </h3>
            <div className="form-section-content-modern">
              <div className="form-group-modern">
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  disabled={!editMode}
                  maxLength="500"
                  rows="4"
                  placeholder="Write a brief professional bio about your expertise and experience..."
                />
                <small className="char-count-modern">{formData.bio.length}/500 characters</small>
              </div>
            </div>
          </div>
        </div>

        {editMode && (
          <div className="form-actions-modern">
            <button
              type="button"
              className="btn-cancel-modern"
              onClick={() => {
                setEditMode(false);
                fetchProfile();
              }}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-save-modern"
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="saving-spinner"></div>
                  Saving...
                </>
              ) : (
                <>
                  <i data-feather="check"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default DoctorProfile;
